import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useMemo } from 'react';
import { useGetCollectionQuery } from '../../../services/entities/impl/blfApi/endpoints/collections';
import { Block } from '../../../services/entities/impl/blfApi/schemas/block';
import { Collection } from '../../../services/entities/impl/blfApi/schemas/collection';
import { CollectionPoint } from '../types';
import { useGetCollectionBlocks } from './useGetCollectionBlocks';
import { useGetCollectionPoints } from './useGetCollectionPoints';

type CollectionDetails = {
  metadata?: Collection;
  points?: CollectionPoint[];
  pointsLoading?: boolean;
  blocks?: Block[];
  blocksLoading?: boolean;
  detailsLoading?: boolean;
};

export const useCollectionDetails = (
  id: Collection['id'] | undefined
): CollectionDetails => {
  // Get metadata about the Collection
  const { currentData: metadata, isLoading: metadataLoading } =
    useGetCollectionQuery(id ?? skipToken);

  /**
   * Load Point scan data for the Collection
   */
  const { points, isLoading: pointsLoading } = useGetCollectionPoints(id);

  /**
   * Load Block data for the Collection
   */
  const { data: blockPaginatedData, isLoading: blocksLoading } =
    useGetCollectionBlocks(id);

  // Aggregate data into a single details object
  const collectionDetails = useMemo<CollectionDetails>(() => {
    return {
      metadata,
      points,
      pointsLoading,
      blocks: blockPaginatedData?.items,
      blocksLoading,
      detailsLoading: metadataLoading || pointsLoading || blocksLoading,
    };
  }, [
    metadata,
    points,
    pointsLoading,
    blockPaginatedData?.items,
    blocksLoading,
  ]);

  return collectionDetails;
};
